<script setup lang="ts">
import type { Work } from '~/composables/useApi'
import { PUBLIC_GALLERY_PATH } from '~/composables/usePlaySignupCta'

interface Props {
  sessionUrl?: string
  shareSlug?: string
  shareTitle?: string
  author?: string
  gamepadCompatible?: boolean
  work?: Work | null
  /**
   * #663: Pyodide built-in package names to preload (e.g. ["numpy"]). Passed
   * through to ``<pyxel-play packages="...">`` so Pyxel calls
   * ``pyodide.loadPackage()`` at boot. Empty → no extra packages.
   */
  pyodidePackages?: string[]
  /**
   * #450: cloud-save scope (app identity) from ``CreateSessionResponse.saveScope``.
   * Used to namespace localStorage for unauthenticated players on the public page.
   */
  saveScope?: string | null
}

const props = defineProps<Props>()

const playSession = usePlaySession()

const appRootUrl = ref<string>('')
const appEntryPoint = ref<string>('')
// #450: session id for cloud save (parsed from sessionUrl). Empty → cloud save off.
const cloudSaveSessionId = ref<string>('')

// いいね/共有の共通ロジック（#443）。#751: いいねはログイン必須。未ログインで押したら
// usePlayActions が全プレイシェル共通でサインイン CTA を開く。
const {
  isLiked,
  displayLikes,
  linkCopied,
  canShare,
  fetchLikeState,
  onLikeClick,
  shareOnX,
  copyLink,
} = usePlayActions({
  shareSlug: () => props.shareSlug,
  shareTitle: () => props.shareTitle,
})

const infoDialogOpen = ref(false)

// #795: 未ログインで十分に遊んだ人／セッション内 2 作品目の人に、離脱の瞬間だけ
// サインアップ CTA を差し込む。CTA が出た場合は離脱を保留し、「今はしない」で閉じられて
// から改めて離脱する。
//
// #821: 発火点はヘッダーの「戻る」ボタン 1 箇所だった（prod 30 日で shown 3 件＝露出率
// 約 2%）。SPA のルート遷移（作者ページへのリンク・ブラウザバック）でも判定するよう
// route guard を足し、離脱全般へ広げる。
const { notePlayStart, maybeOpenOnLeave, dismissCount } = usePlaySignupCta()
const { trackPlayMoreCta } = useFunnelEvent()
let mountedAt = 0
// 離脱の向き（push か popstate か）を見分けるための、このページの履歴位置。
// vue-router が history.state に載せている `position` を借りる。
let mountedHistoryPosition = 0
// CTA を出して保留した離脱の再開手段。保留していないときは null。
let resumeLeave: (() => void) | null = null

const historyPosition = (): number => {
  const state = window.history.state as { position?: number } | null
  return typeof state?.position === 'number' ? state.position : mountedHistoryPosition
}

onMounted(async () => {
  mountedAt = Date.now()
  mountedHistoryPosition = historyPosition()
  notePlayStart()
  // ヘッダーの出口は必ず出る（ヘッダー自体は 10 秒で自動格納されるが、上端の
  // 再表示ストリップでいつでも戻せる）。露出も数えておくと、リンクが静かに
  // 消えたとき（レイアウト変更・prop の渡し忘れ）に shown が 0 に落ちて分かる。
  trackPlayMoreCta('shown', 'header')

  if (props.sessionUrl) {
    const url = new URL(props.sessionUrl)
    const pathTokens = url.pathname.split('/')
    const filename = pathTokens.pop()!
    appEntryPoint.value = filename
    appRootUrl.value = url.origin + pathTokens.join('/')

    const sessionId = pathTokens.pop() || ''
    playSession.setSessionId(sessionId)
    cloudSaveSessionId.value = sessionId
  }

  await fetchLikeState()
})

onUnmounted(() => {
  playSession.resetSessionId()
})

const leave = () => {
  if (window.history.length > 1) {
    useRouter().back()
  }
  else {
    navigateTo('/')
  }
}

const onClickBack = () => {
  // route guard より先にここで判定する（#821 で guard に一本化してはいけない）。
  // 直リンクで入ってきた人は leave() の back() が外部オリジンへの離脱になり、
  // ルート遷移が起きないので guard は走らない。kitao referral 経由（流入の 35%）は
  // まさにこの形なので、ボタン側の判定を落とすと既存の露出を失う。
  if (maybeOpenOnLeave(Date.now() - mountedAt)) {
    resumeLeave = leave
    return
  }
  leave()
}

// #821: SPA のルート遷移で離脱するとき（ヘッダーの作者リンク・ブラウザバック・
// アプリ内リンク）も判定する。CTA を出したら遷移を保留し、「今はしない」で再開する。
//
// guard をページ（`pages/works/[...slug].vue`）ではなく WorkPlay に置くのは、
// WorkPlay がセッション取得後にしかマウントされないため。ページ側に上げると
// セッション取得前の `navigateTo('404')` まで保留対象になる。
//
// 注意: `/works/A` → `/works/B` は同じ route record 内の params 遷移なので leave では
// なく **update**（guard は走らない）。プレイ面に作品→作品のリンクが増えたら、
// そこは `onBeforeRouteUpdate` で拾う必要がある。現状そういうリンクは無い。
//
// この「離脱を保留して割り込む」型そのものは
// doc/developers/frontend_ui_patterns.md に切り出してある。
onBeforeRouteLeave((to) => {
  // #844: 公開ギャラリーへの移動は**離脱ではなく回遊**。ここで CTA を出すと、
  // せっかく「ほかの作品」を押した人の前にダイアログが割り込んで回遊を殺す
  // （しかも 1 日 1 回の枠をそこで使い切る）。成功として素通りさせる。
  if (to.path === PUBLIC_GALLERY_PATH) return true

  if (!maybeOpenOnLeave(Date.now() - mountedAt)) return true

  // ブラウザバック（popstate）はブラウザ側の履歴位置が既に動いている。guard で
  // 中断すると vue-router が位置を戻すので、再開は push ではなく同じ差分の go()
  // でやり直す。push で戻すと履歴が伸び、「戻る」がもう一度作品ページに落ちる。
  const delta = historyPosition() - mountedHistoryPosition
  const router = useRouter()
  resumeLeave = delta !== 0 ? () => router.go(delta) : () => navigateTo(to.fullPath)
  return false
})

watch(dismissCount, () => {
  const resume = resumeLeave ?? leave
  resumeLeave = null
  resume()
})
</script>

<template>
  <PlayImmersiveHeader
    :title="shareTitle"
    :author="author"
    :author-slug="work?.authorSlug"
    :can-share="canShare"
    :is-liked="isLiked"
    :display-likes="displayLikes"
    :work="work"
    :more-works-to="PUBLIC_GALLERY_PATH"
    @back="onClickBack"
    @more-works="trackPlayMoreCta('clicked', 'header')"
    @like="onLikeClick"
    @share-x="shareOnX"
    @copy-link="copyLink"
    @open-info="infoDialogOpen = true"
  />
  <div class="work-play__player">
    <PyxelPlayer
      v-if="appRootUrl && appEntryPoint"
      mode="play"
      fill-parent
      :enable-gamepad="gamepadCompatible"
      :root="appRootUrl"
      :entrypoint="appEntryPoint"
      :packages="props.pyodidePackages?.length ? props.pyodidePackages.join(',') : undefined"
      :cloud-save-session-id="cloudSaveSessionId || undefined"
      :cloud-save-scope="props.saveScope ?? null"
    />
  </div>

  <v-snackbar
    v-model="linkCopied"
    location="bottom"
    :timeout="2500"
    color="blue-grey-darken-3"
  >
    リンクをコピーしました
  </v-snackbar>

  <DialogWorkInfoDialog
    v-model="infoDialogOpen"
    :work="work"
  />
</template>

<style scoped>
.work-play__player {
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
  background: #000;
  /* 非全画面時の視覚シグナル: 微細な内側影で「枠のあるビュー」を表現。
     Alt+Enter で全画面化すると影は画面外に出るので自然に消える */
  box-shadow: inset 0 0 24px rgba(255, 255, 255, 0.05);
}
</style>
